import {
    createContext,
    useCallback,
    useContext,
    useRef,
    useState,
    type Dispatch,
    type ReactNode,
    type SetStateAction,
} from "react";

export type SmileSaveHandler = () => Promise<boolean>;

type SmileUnsavedChangesContextValue = {
    hasUnsavedChanges: boolean;
    setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>;
    registerSaveHandler: (handler: SmileSaveHandler | null) => void;
    runSaveHandler: () => Promise<boolean>;
};

const SmileUnsavedChangesContext = createContext<SmileUnsavedChangesContextValue | null>(null);

export function SmileUnsavedChangesProvider({ children }: { children: ReactNode }) {
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const saveHandlerRef = useRef<SmileSaveHandler | null>(null);

    const registerSaveHandler = useCallback((handler: SmileSaveHandler | null) => {
        saveHandlerRef.current = handler;
    }, []);

    const runSaveHandler = useCallback(async () => {
        if (!saveHandlerRef.current) return false;
        return saveHandlerRef.current();
    }, []);

    return (
        <SmileUnsavedChangesContext.Provider
            value={{
                hasUnsavedChanges,
                setHasUnsavedChanges,
                registerSaveHandler,
                runSaveHandler,
            }}
        >
            {children}
        </SmileUnsavedChangesContext.Provider>
    );
}

export function useSmileUnsavedChanges() {
    const context = useContext(SmileUnsavedChangesContext);
    if (!context) {
        throw new Error("useSmileUnsavedChanges must be used within SmileUnsavedChangesProvider");
    }
    return context;
}
